<template>
  <span>
    <v-btn @click="openDialog" color="grey lighten-4" data-cy="saveFilterButton" elevation="0" small>
      <v-icon class="mr-2" color="primary" x-small>fas fa-save</v-icon> Save Filters...
    </v-btn>

    <v-dialog v-model="dialog" persistent max-width="600">
      <v-card>
        <v-card-title class="text-h5">
          Save Filters
        </v-card-title>
        <v-divider class="mb-3"></v-divider>
        <v-card-text class="mt-3">
          <div>
            Provide a unique name to save these filters:
          </div>
          <v-form class="mt-5" v-model="formIsValid">
            <v-text-field
              :rules="[requiredField, presetNameIsUnique]"
              clearable
              data-cy="filterPresetName"
              label="Filter Preset Name"
              outlined
              v-model="presetName"
            ></v-text-field>
          </v-form>
          <div class="caption pl-2">
            {{ filterList.length > 1 ? `${filterList.length} Filters` : "Filter" }} to Save:
          </div>
          <v-card outlined color="grey lighten-3">
            <v-card-text class="px-6">
              <filter-list :filterList="filterList"></filter-list>
            </v-card-text>
          </v-card>
        </v-card-text>
        <v-card-text v-if="conflictingPreset && !isSubmitted">
          <div>
            <v-alert dense outlined type="error"
              >An existing filter preset "{{ conflictingPreset.name }}" already contains these filters.</v-alert
            >
          </div>
        </v-card-text>
        <v-card-actions class="pb-3 pr-6">
          <v-spacer></v-spacer>
          <v-btn text @click="dialog = false" data-cy="dismissCreateFilterPresetFormButton">
            Cancel
          </v-btn>
          <v-btn
            :disabled="!formIsValid || !!conflictingPreset"
            @click="savePreset"
            color="primary"
            data-cy="createFilterPresetButton"
            elevation="0"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </span>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { intersectionWith, isEqual } from "lodash";

import { formatFilterList } from "../lib/formatters";
import FilterList from "./FilterList.vue";
import ValidationMap from "../lib/validationFunctionMap";

export default {
  components: {
    FilterList,
  },
  computed: {
    conflictingPreset() {
      return this.presetList.find(preset => {
        const largestFilterListSize = Math.max(this.filterList.length, preset.filterList.length);
        return (
          intersectionWith(this.formatFilterList(preset.filterList), this.formatFilterList(this.filterList), isEqual)
            .length === largestFilterListSize
        );
      });
    },
    ...mapState({
      presetList: state => state.filterPreset.list,
    }),
  },
  async created() {
    try {
      await this.getFilterPresetList();
    } catch (e) {
      console.error(e);
    }
  },
  data() {
    return {
      dialog: false,
      formIsValid: false,
      isSubmitted: false,
      presetName: "",
      requiredField: ValidationMap.requiredField,
    };
  },
  methods: {
    formatFilterList(filterList) {
      return formatFilterList(filterList);
    },
    openDialog() {
      this.presetName = "";
      this.dialog = true;
    },
    presetNameIsUnique(presetName) {
      if (!presetName) {
        return true;
      }

      const duplicatePresetName = this.presetList.find(preset => preset.name === presetName);

      if (duplicatePresetName) {
        return "Filter preset name already exists";
      }

      return true;
    },
    async savePreset() {
      if (!this.presetName || !this.filterList.length) {
        return;
      }

      this.isSubmitted = true;

      try {
        await this.createFilterPreset({
          filterList: this.filterList,
          name: this.presetName,
        });
      } catch (e) {
        console.error(e);
        this.$notifyError({
          message: "Failed to save filter preset",
        });
        return;
      }
      this.dialog = false;
      this.$notifySuccess({
        message: `Filter preset '${this.presetName}' successfully saved`,
      });
      this.presetName = "";
    },
    ...mapActions({
      createFilterPreset: "filterPreset/create",
      getFilterPresetList: "filterPreset/list",
    }),
  },
  props: {
    filterList: {
      required: true,
      type: Array,
    },
  },
};
</script>

<style></style>
